/**
 * The one and only `HTMLAudioElement` in the application (spec.md §13.2).
 *
 * This module is the **single** legal construction site for an audio element; the
 * `bl/no-new-audio` lint rule names this exact path in its allow-list and reports the
 * constructor, the `createElement` form and a JSX `<audio>` anywhere else in `apps/web`.
 * `element.test.ts` re-checks the same thing by source grep, which is why no comment in
 * `apps/web/src` may spell either construction form out.
 *
 * Why the whole app shares one element, and why it is created outside React's tree:
 *
 * - Playback restrictions are installed **per element** at construction (WebKit's
 *   `MediaElementSession`, Blink's `AutoplayPolicy.locked_pending_user_gesture_`) and are
 *   cleared **permanently for that element** by its first gesture-driven `play()`. They are
 *   *not* re-installed when `src` changes or `load()` runs. A freshly constructed element
 *   per track — or an `<audio>` that React unmounts and remounts — is therefore *locked
 *   again*, and every subsequent `play()` rejects with `NotAllowedError`. The symptom is
 *   nasty precisely because it is partial: desktop is fine, the first track on mobile is
 *   fine, and nothing after it plays.
 * - Two live elements also mean two claimants for the single audio focus Telegram's webview
 *   grants: iOS silences one, Android pauses both.
 *
 * Element configuration:
 *
 * - `preload = 'none'` — nothing is fetched until the user's first tap. On boot we have no
 *   gesture, and a speculative fetch of a 8 MB track on a metered mobile connection is a
 *   real cost. {@link unlockWith} raises it to `'metadata'` once the element is unlocked.
 * - `playsInline = true` — an audio-only element never goes fullscreen, but Telegram's iOS
 *   webview shares its media-session configuration with video, and the attribute is what
 *   keeps WebKit from ever considering a fullscreen presentation. It is typed on
 *   `HTMLVideoElement` only, hence the narrow cast below.
 * - **`crossOrigin` is deliberately not set.** The media is same-origin (Bot API 10.2
 *   requires the entire Mini App to be served from one origin) and nothing feeds a Web
 *   Audio graph, so there is no reason to opt into CORS — and opting in would make the
 *   element's request differ from the {@link prefetchHead} request, splitting the HTTP
 *   cache entry and wasting the prefetch.
 *
 * Everything else in `engine/` takes a {@link MediaElementLike} so it can be driven by a
 * fake in tests; the exported {@link audio} singleton is the production default.
 */

/* ══ structural types ══════════════════════════════════════════════════════════════ */

/**
 * The `TimeRanges` surface the engine reads. Deliberately structural: the real
 * `TimeRanges` is not constructible from script, so a fake has to be shape-compatible
 * rather than an instance.
 */
export interface TimeRangesLike {
  readonly length: number;
  start(index: number): number;
  end(index: number): number;
}

/** The `MediaError` surface the engine reads. Only `code` is ever meaningful to us. */
export interface MediaErrorLike {
  readonly code: number;
}

/**
 * The subset of `HTMLMediaElement` the engine touches.
 *
 * `HTMLAudioElement` satisfies it structurally, so production code passes the real
 * element and tests pass a recorder. `fastSeek` is optional here because it is optional
 * *at runtime* — Chromium declares it in the IDL but does not implement it — even though
 * `lib.dom.d.ts` types it as required.
 */
export interface MediaElementLike {
  src: string;
  currentTime: number;
  readonly duration: number;
  readonly paused: boolean;
  readonly ended: boolean;
  playbackRate: number;
  preload: string;
  readonly readyState: number;
  readonly networkState: number;
  readonly buffered: TimeRangesLike;
  readonly seekable: TimeRangesLike;
  readonly error: MediaErrorLike | null;
  play(): Promise<void>;
  pause(): void;
  load(): void;
  fastSeek?(time: number): void;
  addEventListener(
    type: string,
    listener: (event: Event) => void,
    options?: boolean | AddEventListenerOptions,
  ): void;
  removeEventListener(
    type: string,
    listener: (event: Event) => void,
    options?: boolean | EventListenerOptions,
  ): void;
}

/* ══ HTMLMediaElement constants, named ═════════════════════════════════════════════ */

/** `HTMLMediaElement.readyState` values, by name (spec.md §13.6). */
export const READY_STATE = {
  /** No information; `duration` is `NaN`. */
  HAVE_NOTHING: 0,
  /** Metadata is available — the only state in which `duration` can be trusted. */
  HAVE_METADATA: 1,
  HAVE_CURRENT_DATA: 2,
  /** Playback can advance. */
  HAVE_FUTURE_DATA: 3,
  HAVE_ENOUGH_DATA: 4,
} as const;

/** `HTMLMediaElement.networkState` values, by name. */
export const NETWORK_STATE = {
  EMPTY: 0,
  IDLE: 1,
  LOADING: 2,
  /** After an error this means the element needs a fresh `src` **and** a `load()`. */
  NO_SOURCE: 3,
} as const;

/** `MediaError.code` values, by name (spec.md §13.6). */
export const MEDIA_ERROR = {
  ABORTED: 1,
  NETWORK: 2,
  /** A truncated range response surfaces here, not as a network error. */
  DECODE: 3,
  SRC_NOT_SUPPORTED: 4,
} as const;

/* ══ the singleton ═════════════════════════════════════════════════════════════════ */

/**
 * `playsInline` is declared on `HTMLVideoElement` in `lib.dom.d.ts`, but the attribute is
 * honoured on any media element by WebKit. Reflecting that with a one-property
 * intersection is more honest than widening the element's type.
 */
type InlinePlayable = { playsInline: boolean };

function createSingleton(): HTMLAudioElement {
  const element = new Audio();
  element.preload = 'none';
  (element as HTMLAudioElement & InlinePlayable).playsInline = true;
  return element;
}

/**
 * The application's audio element. Created at module evaluation, never replaced, never
 * removed, never adopted into a React tree.
 *
 * It is intentionally **not** appended to the document: an unattached media element plays
 * exactly the same, and keeping it out of the DOM makes it impossible for a component
 * unmount to take it down with it.
 */
export const audio: HTMLAudioElement = createSingleton();

/**
 * The singleton, as the structural type the rest of the engine consumes.
 *
 * Prefer this over importing {@link audio} where you only need the engine surface — it
 * keeps call sites honest about the fact that a fake is substitutable.
 */
export function getAudioElement(): MediaElementLike {
  return audio;
}
